import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldCheck, Plus, Trash2, Settings2, Crown, Loader2, Save } from 'lucide-react';

// Regla canonica en lib/platformOwner.js (antes el correo estaba en duro aqui).
import { isPlatformOwner, isDios } from '@/lib/platformOwner';

const PERM_MODULES = [
  { key: 'companies', label: 'Cuentas' },
  { key: 'analytics', label: 'Uso IA' },
  { key: 'platform', label: 'Plataforma' },
  { key: 'plans', label: 'Planes' },
  { key: 'properties', label: 'Propiedades' },
  { key: 'reports', label: 'Reportes' },
  { key: 'superadmins', label: 'Super Admins' },
  { key: 'integrations', label: 'Integraciones' },
  { key: 'projects', label: 'Proyectos' },
  { key: 'academy', label: 'Academy' },
  { key: 'audit', label: 'Bitacora' },
];

export default function SuperAdminsPage() {
  const { user } = useAuth();
  const isOwner = isPlatformOwner(user);
  const dios = isDios(user);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', platform_role: 'superadmin', sa_permissions: {} });
  const [saving, setSaving] = useState(false);
  const [permDialog, setPermDialog] = useState(null);
  const [permForm, setPermForm] = useState({});
  const [permSaving, setPermSaving] = useState(false);

  const load = () => {
    api.get('/admin/superadmins').then(r => { setAdmins(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/superadmins', form);
      toast.success('Super Admin creado');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', platform_role: 'superadmin', sa_permissions: {} });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este Super Admin?')) return;
    try {
      await api.delete(`/admin/superadmins/${id}`);
      toast.success('Super Admin eliminado');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openPerms = (admin) => {
    setPermDialog(admin.id);
    setPermForm(admin.sa_permissions || {});
  };

  const savePerms = async () => {
    setPermSaving(true);
    try {
      await api.put(`/admin/superadmins/${permDialog}/permissions`, { sa_permissions: permForm });
      toast.success('Permisos actualizados');
      setPermDialog(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setPermSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#A5D62C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="superadmins-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Personal de Plataforma</h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            {isOwner ? 'Gestiona Owners, Super Admins, Gerentes y Vendedores de plataforma' : 'Vista de personal del sistema'}
          </p>
        </div>
        {isOwner && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold rounded-lg text-xs" data-testid="create-superadmin-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Usuario de Plataforma
          </Button>
        )}
      </div>

      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardContent className="p-6">
          {admins.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">No hay super admins.</p>
          ) : (
            <div className="space-y-2">
              {admins.map(a => {
                const isThisOwner = isPlatformOwner(a);
                const isThisDios = isDios(a);
                const canPerms = isOwner && !isThisOwner;
                const canDelete = isOwner && !isThisDios && (dios || !isThisOwner);
                return (
                  <div key={a.id} className="flex items-center justify-between p-4 rounded-lg bg-black/20 border border-white/5" data-testid={`sa-row-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isThisOwner ? 'bg-amber-500/10' : 'bg-[#A5D62C]/10'}`}>
                        {isThisOwner ? <Crown className="w-5 h-5 text-amber-400" /> : <ShieldCheck className="w-5 h-5 text-[#A5D62C]" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{a.name}</p>
                        <p className="text-xs text-muted-foreground">{a.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isThisOwner ? (
                        <Badge className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20">Owner</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-[#A5D62C]/10 text-[#A5D62C] border-[#A5D62C]/20">{a.platform_role === 'manager' ? 'Gerente' : a.platform_role === 'vendor' ? 'Vendedor' : 'Super Admin'}</Badge>
                      )}
                      {isOwner && (canPerms || canDelete) && (
                        <>
                          {canPerms && (
                          <Button variant="ghost" size="sm" onClick={() => openPerms(a)} className="h-8 w-8 p-0 text-white/30 hover:text-white/70" title="Permisos" data-testid={`sa-perms-${a.id}`}>
                            <Settings2 className="w-4 h-4" />
                          </Button>
                          )}
                          {canDelete && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="h-8 w-8 p-0 text-red-400/40 hover:text-red-400" data-testid={`sa-delete-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md bg-[#0a0c12] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#A5D62C]" /> Nuevo Usuario de Plataforma
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Nombre</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Nombre completo" className="bg-white/[0.04] border-white/[0.06]" data-testid="sa-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="email@dominio.com" className="bg-white/[0.04] border-white/[0.06]" data-testid="sa-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Contrasena</Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required placeholder="Min 6 caracteres" className="bg-white/[0.04] border-white/[0.06]" data-testid="sa-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Rol de plataforma</Label>
              <select value={form.platform_role} onChange={e => setForm({...form, platform_role: e.target.value})} className="w-full bg-white/[0.04] border border-white/[0.06] rounded-md px-3 py-2 text-sm text-white" data-testid="platform-role-select">
                <option value="superadmin">Super Admin</option>
                <option value="manager">Gerente</option>
                <option value="vendor">Vendedor</option>
                {dios && <option value="owner">Owner</option>}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Permisos por Modulo</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {PERM_MODULES.map(m => {
                  const checked = form.sa_permissions?.[m.key] !== false;
                  return (
                    <label key={m.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-[10px] transition-colors ${checked ? 'bg-[#A5D62C]/5 border-[#A5D62C]/20 text-[#A5D62C]' : 'bg-black/20 border-white/10 text-white/30'}`}>
                      <input type="checkbox" checked={checked} onChange={() => setForm(f => ({ ...f, sa_permissions: { ...f.sa_permissions, [m.key]: !checked } }))} className="sr-only" />
                      <div className={`w-3 h-3 rounded border flex items-center justify-center ${checked ? 'bg-[#A5D62C] border-[#A5D62C]' : 'border-white/20'}`}>
                        {checked && <svg className="w-2 h-2 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      {m.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs" data-testid="submit-create-sa">
              {saving ? 'Creando...' : 'Crear Super Admin'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={!!permDialog} onOpenChange={v => { if (!v) setPermDialog(null); }}>
        <DialogContent className="max-w-sm bg-[#0a0c12] border-white/[0.06]" data-testid="sa-perms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#A5D62C]" /> Permisos del Modulo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {PERM_MODULES.map(m => (
              <div key={m.key} className="flex items-center justify-between py-1.5">
                <span className="text-xs text-white/70">{m.label}</span>
                <Switch checked={permForm[m.key] !== false} onCheckedChange={v => setPermForm({ ...permForm, [m.key]: v })} data-testid={`perm-toggle-${m.key}`} />
              </div>
            ))}
            <Button onClick={savePerms} disabled={permSaving} className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black text-xs font-bold" data-testid="save-sa-perms">
              {permSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Guardar Permisos
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
